/**
 * Phase 5a — Razorpay subscription checkout helpers (frontend).
 *
 * The frontend only ever handles the PUBLIC Razorpay Key ID + a subscription_id
 * returned by our backend. The Key Secret + Webhook Secret never reach the
 * client. A workspace becomes "paid" only when our backend receives a
 * signature-verified webhook — the checkout success handler here is NOT trusted
 * for activation; we poll /billing/info (driven by the webhook) instead.
 */
import api from "./api";

const RZP_SCRIPT = "https://checkout.razorpay.com/v1/checkout.js";

export function loadRazorpayScript() {
  return new Promise((resolve, reject) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = RZP_SCRIPT;
    s.async = true;
    s.onload = () => resolve(true);
    s.onerror = () => reject(new Error("Could not load Razorpay checkout."));
    document.body.appendChild(s);
  });
}

export function currencySymbol(code) {
  return { USD: "$", INR: "₹", EUR: "€", GBP: "£" }[code] || `${code} `;
}

/**
 * Create a subscription on the backend, then open Razorpay checkout.
 * Resolves with { status: "submitted" | "dismissed" | "failed" }.
 * `onState` is called with lifecycle hints for the UI.
 */
export async function startSubscriptionCheckout({ period, user, onState }) {
  const { data } = await api.post("/billing/subscription", { period });
  await loadRazorpayScript();

  return new Promise((resolve) => {
    const options = {
      key: data.razorpay_key_id,
      subscription_id: data.subscription_id,
      name: "Hiretape",
      description: `${period === "annual" ? "Annual" : "Monthly"} plan — ${data.quantity} seat(s)`,
      prefill: { email: user?.email || "", name: user?.name || "" },
      theme: { color: "#047857" },
      handler: () => {
        onState?.("submitted");
        resolve({ status: "submitted" });
      },
      modal: {
        ondismiss: () => {
          onState?.("dismissed");
          resolve({ status: "dismissed" });
        },
      },
    };
    const rzp = new window.Razorpay(options);
    rzp.on("payment.failed", () => {
      onState?.("failed");
    });
    rzp.open();
  });
}

/** Poll /billing/info until the subscription reports active (webhook-driven). */
export async function pollSubscriptionActive({ tries = 12, interval = 3000 } = {}) {
  for (let i = 0; i < tries; i += 1) {
    try {
      const { data } = await api.get("/billing/info");
      if (data.subscription_status === "active") return data;
    } catch {
      /* keep polling */
    }
    await new Promise((r) => setTimeout(r, interval));
  }
  return null;
}

/**
 * Create a PayPal subscription on the backend, then full-page redirect to
 * PayPal's hosted approval page. Activation is webhook-driven — PayPal
 * returns the user to /settings?paypal=return where BillingPanel polls
 * /billing/info (never trusts the redirect itself).
 */
export async function startPayPalCheckout({ period }) {
  const { data } = await api.post("/billing/paypal/subscription", { period });
  if (!data.approve_url) throw new Error("PayPal did not return an approval link.");
  window.location.assign(data.approve_url);
  return data;
}
